import { rgbaToThumbHash } from "thumbhash";
import { ALLOWED_IMAGE_TYPES } from "@/config/constants";
import { uint8ArrayToBase64 } from "@/lib/dataConversions";
import { getImageMetadata } from "./createThumbnail";

// ThumbHash operates on tiny images; the reference implementation recommends a
// maximum dimension of 100px. Larger inputs just make encoding slower.
const THUMBHASH_MAX_DIM = 100;

type Dimensions = { width: number; height: number };

export type MediaPreviewMeta = {
  /** Base64 ThumbHash — a ~25 byte blurred-preview placeholder. */
  placeholder?: string;
  /** Original media pixel dimensions (for aspect-correct bubbles). */
  width?: number;
  height?: number;
};

/**
 * Encode raw RGBA pixels into a base64 ThumbHash. Pure (no DOM) — the canvas
 * extraction lives in `blobToThumbHash`; this is split out so the encode path
 * is unit-testable without a real canvas.
 */
export function rgbaToPlaceholder(
  width: number,
  height: number,
  rgba: Uint8Array | Uint8ClampedArray,
): string {
  return uint8ArrayToBase64(rgbaToThumbHash(width, height, rgba));
}

/**
 * Encode a blob (typically the generated thumbnail) into a base64 ThumbHash.
 * Downscales to <=100px first. Returns null on any failure — the placeholder is
 * a nicety and must never block a send (and canvas APIs are unavailable under
 * jsdom, where this simply yields null).
 */
export async function blobToThumbHash(blob: Blob): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    try {
      const scale = Math.min(
        THUMBHASH_MAX_DIM / bitmap.width,
        THUMBHASH_MAX_DIM / bitmap.height,
        1,
      );
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return null;
      }
      ctx.drawImage(
        bitmap,
        0,
        0,
        bitmap.width,
        bitmap.height,
        0,
        0,
        width,
        height,
      );
      const { data } = ctx.getImageData(0, 0, width, height);
      return rgbaToPlaceholder(width, height, data);
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

/**
 * Build the optional preview-meta fields embedded in a media message's
 * (encrypted) AttachmentMeta: a ThumbHash placeholder derived from the
 * thumbnail (images AND videos), plus the original pixel dimensions for images.
 *
 * Every field is best-effort — any failure just omits it, so a send never
 * blocks on preview generation. Dimensions: images decode them here (cheap,
 * the file is at hand); videos can't without a fresh metadata load, so the
 * caller passes `videoDims` read off the element the thumbnail capture already
 * decoded (see getStagedVideoDims) — absent when that capture failed (the
 * placeholder fallback carries no trustworthy dims). Legacy consumers fall
 * back to the ThumbHash's approximate aspect ratio as before.
 */
export async function getMediaPreviewMeta(
  file: File,
  thumbnailBlob: Blob | null,
  videoDims?: Dimensions,
): Promise<MediaPreviewMeta> {
  const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);

  const [dims, placeholder] = await Promise.all([
    isImage
      ? getImageMetadata(file).catch(() => null)
      : Promise.resolve<Dimensions | null>(
          videoDims && videoDims.width > 0 && videoDims.height > 0
            ? videoDims
            : null,
        ),
    thumbnailBlob ? blobToThumbHash(thumbnailBlob) : Promise.resolve(null),
  ]);

  return {
    ...(dims ? { width: dims.width, height: dims.height } : {}),
    ...(placeholder ? { placeholder } : {}),
  };
}
